import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  studentService,
  paymentService,
  batchService,
  STUDENT_TYPES,
  PROFESSIONAL_COURSES,
  SECONDARY_TRADES,
  getTrainingFee,
  formatRwf,
  SECONDARY_TERM_FEE,
} from '../services';
import {
  getBatchStudentCount,
  getBatchCapacity,
  isBatchAtCapacity,
  getBatchSeatsLeft,
  getBatchCapacityMessage,
} from '../utils/batchCapacity';
import ThemeToggle from './ThemeToggle';

const srEase = [0.22, 1, 0.36, 1];

const categorySectionMotion = {
  hidden: { opacity: 0, y: 10 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.32, ease: srEase },
  },
  exit: { opacity: 0, y: -6, transition: { duration: 0.22, ease: srEase } },
};


// ─── Inline styles (replaces StudentRegistration.css) ────────────────────────
const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap');

  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  .sr-page {
    min-height: 100vh;
    background: linear-gradient(145deg, var(--bg) 0%, #eef4fb 52%, #f8fafc 100%);
    font-family: 'Inter', system-ui, -apple-system, sans-serif;
    color: var(--text);
    line-height: 1.6;
  }
  .sr-page button:focus-visible,
  .sr-page input:focus-visible,
  .sr-page select:focus-visible,
  .sr-page textarea:focus-visible,
  .sr-page summary:focus-visible {
    outline: 3px solid rgba(37, 99, 235, 0.25);
    outline-offset: 3px;
  }

  /* ─── Header ─── */
  .sr-header {
    background: var(--background-white);
    border-bottom: 1px solid var(--border-light);
    padding: 14px 32px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    position: sticky;
    top: 0;
    z-index: 100;
    box-shadow: 0 1px 3px rgb(0 0 0 / 0.04);
  }
  .sr-header-right {
    display: flex;
    align-items: center;
    gap: 20px;
    flex-wrap: wrap;
    justify-content: flex-end;
  }
  .sr-staff-login {
    display: inline-flex;
    align-items: center;
    padding: 8px 14px;
    border: 1px solid var(--border-medium);
    border-radius: 8px;
    font-size: 13px;
    font-weight: 600;
    color: var(--primary-blue);
    text-decoration: none;
    background: var(--surface);
    transition: border-color 0.2s, background 0.2s;
    white-space: nowrap;
  }
  .sr-staff-login:hover {
    border-color: var(--primary-blue);
    background: var(--link-hover-bg);
  }
  .sr-logo {
    display: flex;
    align-items: center;
    gap: 16px;
  }
  .sr-logo-image {
    width: 48px;
    height: 48px;
    object-fit: contain;
    flex-shrink: 0;
  }
  .sr-logo-text {
    font-size: 18px;
    font-weight: 700;
    color: var(--text-dark);
    letter-spacing: -0.025em;
  }
  .sr-logo-sub {
    font-size: 12px;
    color: var(--text-medium);
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    display: block;
    margin-top: 2px;
  }
  .sr-header-contact {
    text-align: right;
    font-size: 13px;
    color: var(--text-light);
    line-height: 1.5;
  }
  .sr-header-contact a {
    color: var(--primary-blue);
    text-decoration: none;
    font-weight: 600;
  }
  .sr-header-contact a:hover {
    text-decoration: underline;
  }

  /* ─── Main layout ─── */
  .sr-main {
    max-width: 1120px;
    margin: 0 auto;
    padding: 38px 24px 56px;
  }
  .sr-application-hero {
    max-width: 720px;
    margin: 0 auto 30px;
    text-align: center;
  }
  .sr-registration-layout {
    display: grid;
    grid-template-columns: minmax(0, 1fr) 290px;
    align-items: start;
    gap: 44px;
  }

  /* ─── Form ─── */
  .sr-form-card {
    padding: 0;
  }

  .sr-form-intro-eyebrow {
    display: inline-block;
    background: var(--link-hover-bg);
    color: var(--primary-blue);
    border: 1px solid #bfdbfe;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    padding: 4px 10px;
    border-radius: 6px;
    margin-bottom: 12px;
  }
  .sr-application-hero h2 {
    font-size: 30px;
    font-weight: 700;
    color: var(--text-dark);
    margin-bottom: 8px;
    line-height: 1.25;
    letter-spacing: -0.03em;
  }
  .sr-application-hero p {
    font-size: 15px;
    color: var(--text-light);
    line-height: 1.6;
    max-width: 560px;
  }

  .sr-form-body {
    padding: 0;
  }

  .sr-steps {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-top: 22px;
    color: var(--text-light);
    font-size: 12px;
    font-weight: 600;
    justify-content: center;
  }
  .sr-step {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    white-space: nowrap;
  }
  .sr-step-number {
    display: grid;
    place-items: center;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background: #dbeafe;
    color: var(--primary-blue);
    font-size: 11px;
  }
  .sr-step--active { color: var(--text-dark); }
  .sr-step--active .sr-step-number { background: var(--primary-blue); color: #fff; }
  .sr-step-line { width: 28px; height: 1px; background: var(--border-medium); }

  .sr-error {
    background: #fef2f2;
    border: 1px solid #fecaca;
    border-left: 4px solid var(--error-red);
    padding: 16px 20px;
    border-radius: 8px;
    margin-bottom: 24px;
    font-size: 14px;
    color: var(--error-red);
    font-weight: 500;
  }

  .sr-section-title {
    font-size: 12px;
    font-weight: 700;
    color: var(--text-light);
    margin: 32px 0 16px;
    padding-bottom: 0;
    border-bottom: none;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    display: flex;
    align-items: center;
    gap: 10px;
  }
  .sr-section-title:first-of-type {
    margin-top: 0;
  }
  .sr-section-title::before {
    content: '';
    display: block;
    width: 3px;
    height: 14px;
    background: var(--primary-blue);
    border-radius: 2px;
    flex-shrink: 0;
  }
  .sr-section-title::after {
    content: '';
    flex: 1;
    height: 1px;
    background: var(--border-light);
  }

  .sr-form-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
  }
  .sr-form-row--3 {
    grid-template-columns: 1fr 1fr 1fr;
    gap: 12px;
  }
  .sr-form-row .sr-form-group {
    margin-bottom: 0;
  }

  .sr-form-group {
    display: flex;
    flex-direction: column;
    gap: 6px;
    margin-bottom: 16px;
  }
  .sr-form-group:last-child { margin-bottom: 0; }

  .sr-label {
    font-size: 13px;
    font-weight: 600;
    color: var(--text-medium);
    margin-bottom: 2px;
    display: block;
  }
  .sr-required { color: var(--error-red); margin-left: 4px; }

  .sr-input, .sr-select, .sr-textarea {
    width: 100%;
    padding: 11px 14px;
    border: 1px solid var(--border-medium);
    border-radius: 8px;
    font-family: inherit;
    font-size: 15px;
    color: var(--text-dark);
    background: var(--background-white);
    transition: border-color 0.2s, box-shadow 0.2s;
    outline: none;
  }
  .sr-input::placeholder { 
    color: var(--text-light); 
    font-weight: 400;
  }
  .sr-input:focus, .sr-select:focus, .sr-textarea:focus {
    border-color: var(--primary-blue);
    box-shadow: 0 0 0 3px rgba(30, 64, 175, 0.1);
  }
  .sr-input:disabled, .sr-select:disabled, .sr-textarea:disabled {
    background: var(--background-gray);
    color: var(--text-light);
    cursor: not-allowed;
    border-color: var(--border-light);
  }
  .sr-select {
    -webkit-appearance: none;
    -moz-appearance: none;
    appearance: none;
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='20' height='20' fill='none' viewBox='0 0 24 24'%3E%3Cpath stroke='%236b7280' stroke-width='2' stroke-linecap='round' stroke-linejoin='round' d='M6 9l6 6 6-6'/%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: right 16px center;
    padding-right: 48px;
    cursor: pointer;
  }
  .sr-select option { color: var(--text-dark); }

  .sr-submit {
    margin-top: 28px;
    width: 100%;
    padding: 13px 24px;
    background: linear-gradient(135deg, var(--primary-blue), #2563eb);
    color: white;
    border: none;
    border-radius: 8px;
    font-family: inherit;
    font-size: 15px;
    font-weight: 600;
    cursor: pointer;
    transition: transform 0.2s, box-shadow 0.2s, background 0.2s;
    box-shadow: 0 8px 18px rgba(30, 64, 175, 0.18);
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    min-height: 48px;
  }
  .sr-submit:hover:not(:disabled) {
    background: linear-gradient(135deg, var(--primary-dark), var(--primary-blue));
    box-shadow: 0 10px 22px rgba(30, 64, 175, 0.28);
    transform: translateY(-1px);
  }
  .sr-submit:active:not(:disabled) {
    background: var(--primary-dark);
    box-shadow: 0 4px 10px rgba(30, 64, 175, 0.18);
    transform: translateY(0);
  }
  .sr-submit:disabled {
    background: #9ca3af;
    box-shadow: none;
    cursor: not-allowed;
  }
  .sr-submit-spinner {
    width: 16px;
    height: 16px;
    border: 2px solid rgba(255,255,255,0.3);
    border-top-color: white;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  }
  @keyframes spin { to { transform: rotate(360deg); } }

  /* ─── Category segment control ─── */
  .sr-segment {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
    padding: 0;
    background: transparent;
    border-radius: 0;
    margin-bottom: 4px;
    border: none;
  }
  .sr-segment-btn {
    position: relative;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: flex-start;
    gap: 8px;
    padding: 14px 0;
    border: none;
    border: 1px solid var(--border-light);
    border-bottom: 3px solid var(--border-light);
    background: rgba(255,255,255,0.72);
    cursor: pointer;
    font-family: inherit;
    border-radius: 8px;
    min-height: 64px;
    transition: border-color 0.2s, background 0.2s, box-shadow 0.2s, transform 0.2s;
    text-align: left;
    padding-left: 14px;
    padding-right: 14px;
  }
  .sr-segment-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  .sr-segment-btn.active {
    background: #eff6ff;
    border-color: #bfdbfe;
    border-bottom-color: var(--primary-blue);
    box-shadow: 0 8px 18px rgba(30, 64, 175, 0.1);
  }
  .sr-segment-btn:not(.active):hover:not(:disabled) {
    border-color: #bfdbfe;
    border-bottom-color: var(--primary-blue);
    background: #f8fbff;
    transform: translateY(-1px);
  }
  .sr-segment-content {
    text-align: center;
    flex: 0;
    min-width: 0;
  }
  .sr-form-actions {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 14px;
    margin-top: 32px;
    padding-top: 22px;
    border-top: 1px solid var(--border-light);
  }
  .sr-submit { margin-top: 0; }
  .sr-back-btn {
    min-height: 48px;
    padding: 0 18px;
    border: 1px solid var(--border-medium);
    border-radius: 8px;
    background: var(--background-white);
    color: var(--text-medium);
    font: 600 14px inherit;
    cursor: pointer;
    transition: border-color 0.2s, color 0.2s, background 0.2s, transform 0.2s;
  }
  .sr-back-btn:hover:not(:disabled) {
    border-color: #93c5fd;
    color: var(--primary-blue);
    background: #f8fbff;
    transform: translateY(-1px);
  }
  .sr-review-intro { font-size: 14px; color: var(--text-light); margin: -8px 0 20px; }
  .sr-review-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; }
  .sr-review-card { border-bottom: 1px solid var(--border-light); padding: 0 0 14px; background: transparent; }
  .sr-review-card--wide { grid-column: 1 / -1; }
  .sr-review-label { display: block; color: var(--text-light); font-size: 11px; font-weight: 700; letter-spacing: .06em; text-transform: uppercase; margin-bottom: 6px; }
  .sr-review-value { color: var(--text-dark); font-size: 14px; font-weight: 600; overflow-wrap: anywhere; }
  .sr-segment-abbr {
    width: 28px;
    height: 28px;
    border-radius: 6px;
    background: var(--surface);
    border: 1px solid var(--border-light);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 10px;
    font-weight: 800;
    color: var(--primary-blue);
    letter-spacing: 0.02em;
    flex-shrink: 0;
  }
  .sr-segment-btn.active .sr-segment-abbr {
    background: var(--primary-blue);
    border-color: var(--primary-blue);
    color: #fff;
  }
  .sr-segment-title {
    font-size: 14px;
    font-weight: 700;
    color: var(--text-dark);
    line-height: 1.3;
  }
  .sr-segment-btn:not(.active) .sr-segment-title {
    color: var(--text-medium);
  }
  .sr-category-fields {
    overflow: hidden;
  }
  .sr-course-options {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 10px;
  }
  .sr-course-option {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    padding: 12px;
    border: 1px solid var(--border-medium);
    border-radius: 10px;
    background: var(--background-white);
    cursor: pointer;
    font-size: 13px;
    color: var(--text-medium);
    transition: border-color 0.2s, background 0.2s, box-shadow 0.2s, transform 0.2s;
  }
  .sr-course-option:hover {
    border-color: #93c5fd;
    background: #f8fbff;
    transform: translateY(-1px);
  }
  .sr-course-option:has(input:checked) {
    border-color: var(--primary-blue);
    background: var(--link-hover-bg);
    color: var(--text-dark);
    box-shadow: 0 5px 12px rgba(30, 64, 175, 0.1);
  }
  .sr-course-option input { margin-top: 3px; accent-color: var(--primary-blue); }
  .sr-course-hint { font-size: 12px; color: var(--text-light); margin-top: -2px; }

  /* Optional fields panel */
  .sr-details {
    border: none;
    border-top: 1px solid var(--border-medium);
    border-bottom: 1px solid var(--border-medium);
    border-radius: 0;
    background: transparent;
    margin-top: 16px;
    overflow: hidden;
  }
  .sr-details summary {
    list-style: none;
    cursor: pointer;
    padding: 16px 20px;
    font-size: 14px;
    font-weight: 600;
    color: var(--text-dark);
    display: flex;
    align-items: center;
    justify-content: space-between;
    user-select: none;
    background: transparent;
    border-bottom: 1px solid var(--border-light);
  }
  .sr-details summary::-webkit-details-marker { display: none; }
  .sr-details summary::after {
    content: '+ Show';
    font-size: 13px;
    font-weight: 500;
    color: var(--primary-blue);
  }
  .sr-details[open] summary::after { content: '− Hide'; }
  .sr-details-body {
    padding: 20px;
    background: transparent;
  }

  /* ─── Sidebar ─── */
  .sr-sidebar {
    position: sticky;
    top: 88px;
    display: flex;
    flex-direction: column;
    gap: 18px;
    padding-left: 28px;
    border-left: 1px solid var(--border-light);
  }
  .sr-sidebar-heading {
    font-size: 12px;
    font-weight: 800;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: var(--text-light);
    margin: 2px 0 -4px;
  }
  .sr-sidebar-fee {
    background: transparent;
    border-radius: 0;
    padding: 0 0 20px;
    color: var(--text-dark);
    border: none;
    border-bottom: 1px solid var(--border-light);
    box-shadow: none;
  }
  .sr-fee-badge {
    display: inline-block;
    background: var(--bg);
    border-radius: 6px;
    padding: 4px 10px;
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: var(--text-light);
    margin-bottom: 14px;
  }
  .sr-fee-amount {
    font-size: 32px;
    font-weight: 800;
    line-height: 1;
    margin-bottom: 4px;
    color: var(--primary-blue);
    letter-spacing: -0.03em;
  }
  .sr-fee-currency {
    font-size: 13px;
    font-weight: 500;
    color: var(--text-light);
    margin-bottom: 14px;
  }
  .sr-fee-divider { border: none; border-top: 1px solid var(--border-light); margin: 14px 0; }
  .sr-fee-note {
    font-size: 12px;
    font-weight: 400;
    color: var(--text-light);
    line-height: 1.55;
  }
  .sr-cohort-notice-wrap {
    margin: 18px 0 22px;
    perspective: 800px;
  }
  .sr-cohort-notice {
    position: relative;
    width: 100%;
    background: transparent;
    border: none;
    border-left: 4px solid var(--primary-blue);
    border-radius: 0;
    padding: 16px 40px 16px 18px;
    box-shadow: none;
    pointer-events: auto;
    will-change: transform;
  }
  .sr-cohort-notice.open {
    border-left: 4px solid var(--primary-blue);
  }
  .sr-cohort-notice.closed {
    border-left: 4px solid var(--warning-yellow);
    background: #fffbeb;
  }
  .sr-cohort-notice-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    margin-bottom: 8px;
    padding-right: 4px;
  }
  .sr-cohort-notice-head-left {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
    min-width: 0;
  }
  .sr-cohort-notice-close {
    position: absolute;
    top: 10px;
    right: 10px;
    width: 28px;
    height: 28px;
    border: none;
    border-radius: 6px;
    background: transparent;
    color: var(--text-light);
    font-size: 20px;
    line-height: 1;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    font-family: inherit;
    transition: background 0.15s, color 0.15s;
  }
  .sr-cohort-notice-close:hover {
    background: rgb(0 0 0 / 0.06);
    color: var(--text-dark);
  }
  .sr-cohort-notice.closed .sr-cohort-notice-close:hover {
    background: rgb(180 83 9 / 0.1);
    color: #92400e;
  }
  .sr-cohort-notice-program {
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: var(--primary-blue);
  }
  .sr-cohort-notice.closed .sr-cohort-notice-program {
    color: #b45309;
  }
  .sr-cohort-notice-status {
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    padding: 3px 8px;
    border-radius: 20px;
    flex-shrink: 0;
  }
  .sr-cohort-notice-status--open {
    background: #ecfdf5;
    color: #047857;
    border: 1px solid #a7f3d0;
  }
  .sr-cohort-notice-status--closed {
    background: #fef3c7;
    color: #b45309;
    border: 1px solid #fcd34d;
  }
  .sr-cohort-notice-title {
    font-size: 16px;
    font-weight: 700;
    color: var(--text-dark);
    line-height: 1.35;
    margin-bottom: 6px;
    letter-spacing: -0.02em;
  }
  .sr-cohort-notice-fee {
    font-size: 13px;
    font-weight: 600;
    color: var(--primary-blue);
    margin-bottom: 8px;
  }
  .sr-cohort-notice.closed .sr-cohort-notice-fee {
    color: var(--text-medium);
  }
  .sr-cohort-notice-text {
    font-size: 12px;
    color: var(--text-light);
    line-height: 1.5;
  }
  .sr-cohort-notice.closed .sr-cohort-notice-text {
    color: #92400e;
  }

  .sr-sidebar-features {
    background: transparent;
    border: none;
    border-radius: 0;
    padding: 0;
    box-shadow: none;
  }
  .sr-features-title {
    font-size: 11px;
    font-weight: 700;
    color: var(--text-light);
    margin-bottom: 14px;
    text-transform: uppercase;
    letter-spacing: 0.06em;
  }
  .sr-features-list {
    list-style: none;
    display: flex;
    flex-direction: column;
    gap: 12px;
  }
  .sr-features-list li {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    font-size: 13px;
    color: var(--text-medium);
    line-height: 1.5;
    font-weight: 500;
  }
  .sr-feature-check {
    width: 18px;
    height: 18px;
    border-radius: 50%;
    background: var(--link-hover-bg);
    border: 1px solid #bfdbfe;
    flex-shrink: 0;
    margin-top: 1px;
    position: relative;
  }
  .sr-feature-check::after {
    content: '';
    position: absolute;
    left: 5px;
    top: 3px;
    width: 4px;
    height: 8px;
    border: solid var(--primary-blue);
    border-width: 0 2px 2px 0;
    transform: rotate(45deg);
  }

  /* ─── Footer ─── */
  .sr-footer {
    border-top: 1px solid var(--border-light);
    background: var(--background-white);
    padding: 18px 32px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 12px;
    font-size: 12px;
    color: var(--text-light);
  }
  .sr-footer-links { display: flex; flex-wrap: wrap; gap: 20px; }
  .sr-footer-links a {
    color: var(--text-medium);
    text-decoration: none;
    font-weight: 500;
    transition: color 0.15s;
  }
  .sr-footer-links a:hover { color: var(--primary-blue); }

  /* ─── Modals ─── */
  .sr-overlay {
    position: fixed;
    inset: 0;
    background: rgba(10,22,40,0.85);
    backdrop-filter: blur(6px);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
    padding: 24px;
    animation: fadeIn 0.2s ease;
  }
  @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }

  .sr-modal {
    background: var(--surface);
    border-radius: 12px;
    padding: 36px 32px;
    max-width: 480px;
    width: 100%;
    color: var(--text-dark);
    box-shadow: 0 20px 50px rgb(0 0 0 / 0.18);
    border: 1px solid var(--border-light);
    animation: slideUp 0.25s ease;
    max-height: 90vh;
    overflow-y: auto;
  }
  @keyframes slideUp { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }

  .sr-modal-icon {
    width: 64px;
    height: 64px;
    border-radius: 50%;
    margin: 0 auto 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 24px;
  }
  .sr-modal-icon--success { background: #ecfdf5; color: #047857; border: 1px solid #a7f3d0; }
  .sr-modal-icon--info { background: var(--link-hover-bg); color: #1e40af; border: 1px solid #bfdbfe; }
  .sr-modal-icon--warn { background: #fffbeb; color: #b45309; border: 1px solid #fcd34d; }

  .sr-modal-title {
    font-family: inherit;
    font-size: 26px;
    font-weight: 700;
    text-align: center;
    color: var(--text-dark);
    margin-bottom: 12px;
  }
  .sr-modal-msg {
    font-size: 15px;
    color: #4a5a7a;
    text-align: center;
    line-height: 1.6;
    margin-bottom: 8px;
  }
  .sr-modal-sub {
    font-size: 13px;
    color: #8090b0;
    text-align: center;
    line-height: 1.5;
    margin-bottom: 28px;
  }

  .sr-fee-box {
    background: var(--primary-blue);
    border-radius: 14px;
    padding: 24px;
    text-align: center;
    margin-bottom: 28px;
  }
  .sr-fee-box-label { font-size: 12px; color: rgba(255,255,255,0.5); text-transform: uppercase; letter-spacing: 0.08em; margin-bottom: 8px; }
  .sr-fee-box-amount { font-family: inherit; font-size: 40px; font-weight: 700; color: var(--accent-orange); margin-bottom: 6px; line-height: 1.1; }
  .sr-fee-box-course {
    font-size: 13px;
    font-weight: 600;
    color: var(--accent-orange);
    margin-bottom: 10px;
    line-height: 1.4;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }
  .sr-fee-box-note { font-size: 12px; color: rgba(255,255,255,0.55); line-height: 1.5; }

  .sr-payment-methods {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 14px;
    margin-bottom: 20px;
  }
  .sr-payment-btn {
    padding: 20px 16px;
    border: 1.5px solid rgba(10,22,40,0.15);
    border-radius: 12px;
    background: var(--surface);
    cursor: pointer;
    transition: border-color 0.2s, box-shadow 0.2s, transform 0.15s;
    text-align: center;
    font-family: inherit;
  }
  .sr-payment-btn:hover {
    border-color: var(--accent-orange);
    box-shadow: 0 4px 16px rgba(201,168,76,0.15);
    transform: translateY(-2px);
  }
  .sr-payment-btn-icon { font-size: 28px; margin-bottom: 8px; }
  .sr-payment-btn-label { font-size: 14px; font-weight: 600; color: var(--text-dark); }
  .sr-payment-btn-sub { font-size: 11px; color: #8090b0; margin-top: 3px; }

  .sr-payment-note {
    font-size: 12px;
    color: #8090b0;
    text-align: center;
    margin-bottom: 20px;
    line-height: 1.5;
  }

  .sr-details-card {
    background: var(--surface);
    border: 1.5px solid rgba(10,22,40,0.1);
    border-radius: 12px;
    overflow: hidden;
    margin-bottom: 24px;
  }
  .sr-detail-row {
    display: flex;
    align-items: center;
    padding: 13px 18px;
    border-bottom: 1px solid rgba(10,22,40,0.06);
  }
  .sr-detail-row:last-child { border-bottom: none; }
  .sr-detail-lbl { font-size: 12px; color: #8090b0; font-weight: 600; text-transform: uppercase; letter-spacing: 0.05em; flex: 1; }
  .sr-detail-val { font-size: 14px; color: var(--text-dark); font-weight: 600; }
  .sr-copy-btn {
    background: none;
    border: none;
    cursor: pointer;
    padding: 4px 8px;
    color: var(--accent-orange);
    font-size: 13px;
    border-radius: 6px;
    transition: background 0.15s;
    margin-left: 8px;
  }
  .sr-copy-btn:hover { background: rgba(201,168,76,0.1); }

  .sr-momo-card {
    background: linear-gradient(135deg, #e8f4fd, #ddeeff);
    border: 1.5px solid #c0daf5;
    border-radius: 12px;
    padding: 20px;
    text-align: center;
    margin-bottom: 24px;
  }
  .sr-momo-label { font-size: 11px; font-weight: 700; color: #4080c0; letter-spacing: 0.08em; text-transform: uppercase; margin-bottom: 10px; }
  .sr-momo-code {
    display: block;
    font-family: 'Courier New', monospace;
    font-size: 18px;
    font-weight: 700;
    color: var(--text-dark);
    background: var(--surface);
    border-radius: 8px;
    padding: 10px 16px;
    margin: 8px 0;
    border: 1px solid #c0daf5;
  }
  .sr-momo-instruction { font-size: 12px; color: #6080a0; margin-top: 8px; }

  .sr-modal-back {
    background: none;
    border: none;
    cursor: pointer;
    color: #8090b0;
    font-size: 13px;
    font-family: inherit;
    font-weight: 600;
    padding: 0;
    margin-bottom: 24px;
    display: flex;
    align-items: center;
    gap: 6px;
    transition: color 0.15s;
  }
  .sr-modal-back:hover { color: var(--primary-blue); }

  .sr-modal-method-header {
    display: flex;
    align-items: center;
    gap: 14px;
    margin-bottom: 20px;
  }
  .sr-modal-method-icon {
    width: 48px;
    height: 48px;
    border-radius: 12px;
    background: var(--primary-blue);
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 22px;
    flex-shrink: 0;
  }
  .sr-modal-method-title {
    font-family: inherit;
    font-size: 22px;
    font-weight: 700;
    color: var(--text-dark);
  }
  .sr-modal-method-sub { font-size: 13px; color: #8090b0; margin-top: 2px; }

  .sr-file-upload {
    border: 2px dashed rgba(10,22,40,0.2);
    border-radius: 10px;
    padding: 24px;
    text-align: center;
    cursor: pointer;
    transition: border-color 0.2s, background 0.2s;
    position: relative;
    background: var(--surface);
    margin-bottom: 20px;
  }
  .sr-file-upload:hover, .sr-file-upload.has-file {
    border-color: var(--accent-orange);
    background: rgba(201,168,76,0.04);
  }
  .sr-file-upload input {
    position: absolute;
    inset: 0;
    opacity: 0;
    cursor: pointer;
    width: 100%;
    height: 100%;
  }
  .sr-file-upload-icon { font-size: 28px; margin-bottom: 8px; }
  .sr-file-upload-text { font-size: 14px; color: #6070a0; font-weight: 500; }
  .sr-file-upload-sub { font-size: 11px; color: #b0b8cc; margin-top: 4px; }

  .sr-proof-textarea {
    width: 100%;
    padding: 14px 16px;
    border: 1.5px solid rgba(10,22,40,0.15);
    border-radius: 10px;
    font-family: inherit;
    font-size: 14px;
    color: var(--text-dark);
    resize: vertical;
    outline: none;
    transition: border-color 0.2s, box-shadow 0.2s;
    margin-bottom: 20px;
  }
  .sr-proof-textarea:focus {
    border-color: var(--accent-orange);
    box-shadow: 0 0 0 3px rgba(201,168,76,0.12);
  }

  .sr-modal-primary {
    width: 100%;
    padding: 15px;
    background: linear-gradient(135deg, var(--primary-blue), #2563eb);
    color: white;
    border: none;
    border-radius: 10px;
    font-family: inherit;
    font-size: 15px;
    font-weight: 600;
    cursor: pointer;
    transition: opacity 0.15s, transform 0.15s, box-shadow 0.15s;
    box-shadow: 0 8px 18px rgba(30, 64, 175, 0.2);
    margin-bottom: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
  }
  .sr-modal-primary:disabled { opacity: 0.5; cursor: not-allowed; }
  .sr-modal-primary:hover:not(:disabled) {
    opacity: 0.95;
    transform: translateY(-1px);
    box-shadow: 0 10px 22px rgba(30, 64, 175, 0.28);
  }

  .sr-modal-secondary {
    width: 100%;
    padding: 13px;
    background: transparent;
    color: #8090b0;
    border: 1.5px solid rgba(10,22,40,0.12);
    border-radius: 8px;
    font-family: inherit;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: border-color 0.15s, color 0.15s, background 0.15s, transform 0.15s;
  }
  .sr-modal-secondary:hover {
    border-color: #93c5fd;
    color: var(--text-dark);
    background: #f8fbff;
    transform: translateY(-1px);
  }

  .sr-success-badge {
    background: linear-gradient(135deg, #e8faf0, #d4f5e2);
    border: 1.5px solid #a0d8b4;
    border-radius: 12px;
    padding: 24px;
    text-align: center;
    margin-bottom: 20px;
  }
  .sr-success-badge-icon { font-size: 32px; margin-bottom: 10px; }
  .sr-success-badge-title { font-size: 16px; font-weight: 700; color: #1a6a3a; margin-bottom: 8px; }
  .sr-success-badge-text { font-size: 13px; color: #3a7a52; line-height: 1.5; }

  .sr-modal-error {
    background: #fef2f2;
    border-left: 3px solid var(--error-red);
    padding: 12px 16px;
    border-radius: 8px;
    font-size: 13px;
    color: var(--error-red);
    margin-bottom: 16px;
    font-weight: 500;
  }
  .sr-notification-modal {
    max-width: 440px;
    text-align: center;
  }
  .sr-notification-icon {
    width: 58px;
    height: 58px;
    margin: 0 auto 18px;
    display: grid;
    place-items: center;
    border-radius: 50%;
    background: #fff7ed;
    border: 1px solid #fed7aa;
    color: #c2410c;
    font-size: 25px;
    font-weight: 800;
  }
  .sr-notification-message {
    color: var(--text-medium);
    font-size: 14px;
    line-height: 1.65;
    margin: 0 auto 24px;
    max-width: 340px;
  }

  /* ─── Responsive ─── */
  @media (max-width: 900px) {
    .sr-main {
      padding: 20px 16px 36px;
    }
    .sr-registration-layout {
      grid-template-columns: 1fr;
      gap: 20px;
    }
    .sr-application-hero,
    .sr-form-body {
      padding-left: 20px;
      padding-right: 20px;
    }
    .sr-application-hero { padding-top: 8px; margin-bottom: 22px; }
    .sr-application-hero h2 { font-size: 26px; }
    .sr-steps { gap: 6px; font-size: 11px; overflow-x: auto; padding-bottom: 2px; }
    .sr-step { gap: 5px; }
    .sr-step-line { width: 14px; flex-shrink: 0; }
    .sr-sidebar {
      position: static;
      order: -1;
      padding-left: 0;
      padding-bottom: 20px;
      border-left: none;
      border-bottom: 1px solid var(--border-light);
    }
    .sr-header {
      padding: 12px 16px;
    }
    .sr-header-contact {
      display: none;
    }
    .sr-form-row, .sr-form-row--3 {
      grid-template-columns: 1fr;
    }
    .sr-review-grid { grid-template-columns: 1fr; }
    .sr-review-card--wide { grid-column: auto; }
    .sr-segment {
      grid-template-columns: 1fr;
    }
    .sr-course-options { grid-template-columns: 1fr; }
    .sr-footer {
      padding: 16px;
      flex-direction: column;
      align-items: flex-start;
    }
  }
`;

// ─── isDuplicateError helper ──────────────────────────────────────────────────
const isDuplicateError = (err) => {
  const status = err?.status ?? err?.response?.status;
  const msg = (err?.error || err?.message || '').toString().toLowerCase();
  if (status === 409) return true;
  if (status === 400 && /already|duplicate|registered|exists|same name|name already/.test(msg)) return true;
  return false;
};

const SectionTitle = ({ label }) => (
  <h3 className="sr-section-title">{label}</h3>
);

const Field = ({ label, required, children }) => (
  <div className="sr-form-group">
    <label className="sr-label">{label}{required && <span className="sr-required">*</span>}</label>
    {children}
  </div>
);

const formatBatchDate = (value) => {
  if (!value) return null;
  try {
    return new Date(value).toLocaleDateString(undefined, { month: 'short', year: 'numeric' });
  } catch {
    return null;
  }
};

const getProgramIntakeLabel = (studentType) =>
  studentType === STUDENT_TYPES.SECONDARY ? 'Secondary intake' : 'Professional intake';

const CohortNotice = ({ activeOption, batch, batchStats }) => {
  const [visible, setVisible] = useState(true);
  const atCapacity = Boolean(batch) && isBatchAtCapacity(batch, batchStats);
  const isOpen = Boolean(batch) && !atCapacity;
  const startLabel = formatBatchDate(batch?.startDate);
  const capacity = batch ? getBatchCapacity(batch) : null;
  const seatsLeft = batch ? getBatchSeatsLeft(batch, batchStats) : null;
  const studentCount = batch ? getBatchStudentCount(batch, batchStats) : 0;

  useEffect(() => {
    setVisible(true);
  }, [activeOption.type]);

  return (
    <AnimatePresence mode="wait">
      {visible && (
      <motion.div
        key={activeOption.type}
        className="sr-cohort-notice-wrap"
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 14 }}
        transition={{ duration: 0.32, ease: srEase }}
      >
      <motion.aside
        className={`sr-cohort-notice${isOpen ? ' open' : ' closed'}`}
        role="status"
        aria-live="polite"
        animate={{ y: [0, -8, 0] }}
        transition={{
          y: {
            duration: 4.2,
            repeat: Infinity,
            ease: 'easeInOut',
            times: [0, 0.5, 1],
          },
        }}
      >
        <button
          type="button"
          className="sr-cohort-notice-close"
          onClick={() => setVisible(false)}
          aria-label="Close cohort notification"
        >
          ×
        </button>
        <div className="sr-cohort-notice-head">
          <div className="sr-cohort-notice-head-left">
            <span className="sr-cohort-notice-program">{getProgramIntakeLabel(activeOption.type)}</span>
            <span className={`sr-cohort-notice-status sr-cohort-notice-status--${isOpen ? 'open' : 'closed'}`}>
              {atCapacity ? 'Full' : isOpen ? 'Open' : 'Closed'}
            </span>
          </div>
        </div>
        <div className="sr-cohort-notice-fee">{activeOption.fee}</div>
        {atCapacity ? (
          <>
            <div className="sr-cohort-notice-title">{batch.name}</div>
            <p className="sr-cohort-notice-text">
              This cohort is full ({studentCount}/{capacity} seats). Registration is closed until the center increases capacity.
            </p>
          </>
        ) : isOpen ? (
          <>
            <div className="sr-cohort-notice-title">{batch.name}</div>
            <p className="sr-cohort-notice-text">
              {activeOption.desc}. You will join this cohort when you submit your registration.
              {startLabel ? ` Cohort started ${startLabel}.` : ''}
              {capacity != null && seatsLeft != null
                ? ` ${seatsLeft} seat${seatsLeft === 1 ? '' : 's'} left (${studentCount}/${capacity}).`
                : ''}
            </p>
          </>
        ) : (
          <>
            <div className="sr-cohort-notice-title">Registration not open</div>
            <p className="sr-cohort-notice-text">
              {activeOption.desc}. No active {activeOption.title.toLowerCase()} cohort right now — please try again later or contact the center.
            </p>
          </>
        )}
      </motion.aside>
      </motion.div>
      )}
    </AnimatePresence>
  );
};

// ─── Component ────────────────────────────────────────────────────────────────
const StudentRegistration = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successModalOpen, setSuccessModalOpen] = useState(false);
  const [duplicateModalOpen, setDuplicateModalOpen] = useState(false);
  const [notification, setNotification] = useState({ open: false, title: '', message: '' });
  const [registeredStudent, setRegisteredStudent] = useState(null);
  const [registeredFee, setRegisteredFee] = useState(null);
  const [modalStep, setModalStep] = useState('success');
  const [paymentReceipt, setPaymentReceipt] = useState(null);
  const [paymentProof, setPaymentProof] = useState('');
  const [paymentSubmitted, setPaymentSubmitted] = useState(false);
  const [paymentSubmitting, setPaymentSubmitting] = useState(false);
  const [paymentError, setPaymentError] = useState('');
  const [copied, setCopied] = useState('');
  const [runningBatches, setRunningBatches] = useState([]);
  const [batchStatsMap, setBatchStatsMap] = useState({});
  const [currentStep, setCurrentStep] = useState(1);

  useEffect(() => {
    batchService
      .getRunningBatches()
      .then(async (data) => {
        const list = Array.isArray(data) ? data : [];
        setRunningBatches(list);
        const entries = await Promise.all(
          list.map(async (b) => {
            try {
              const stats = await batchService.getBatchStats(b._id);
              return [b._id, stats];
            } catch {
              return [b._id, { total: 0 }];
            }
          })
        );
        setBatchStatsMap(Object.fromEntries(entries));
      })
      .catch(() => {
        setRunningBatches([]);
        setBatchStatsMap({});
      });
  }, []);

  const getRunningBatchForType = (studentType) => {
    const track = studentType === STUDENT_TYPES.SECONDARY ? 'secondary' : 'professional';
    return runningBatches.find((b) => b.track === track);
  };

  const getInitialFormState = () => ({
    studentType: STUDENT_TYPES.SECONDARY,
    course: '',
    courses: [],
    fullName: '', gender: '', dateOfBirth: '', phone: '', email: '',
    address: '', fieldOfStudy: '', schoolName: '', level: '',
    profession: '', employer: '', yearsOfExperience: '',
    emergencyContact: { name: '', phone: '', relation: '' },
  });
  const [formData, setFormData] = useState(getInitialFormState());

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name.startsWith('emergencyContact.')) {
      const field = name.split('.')[1];
      setFormData(prev => ({ ...prev, emergencyContact: { ...prev.emergencyContact, [field]: value } }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSelectStudentType = (type) => {
    setFormData((prev) => {
      if (prev.studentType === type) return prev;
      return {
        ...getInitialFormState(),
        studentType: type,
        fullName: prev.fullName,
        gender: prev.gender,
        dateOfBirth: prev.dateOfBirth,
        phone: prev.phone,
        email: prev.email,
        address: prev.address,
      };
    });
    setError('');
  };

  const handleCourseToggle = (course) => {
    setFormData((prev) => {
      const courses = prev.courses.includes(course)
        ? prev.courses.filter((selected) => selected !== course)
        : [...prev.courses, course];
      return { ...prev, courses, course: courses[0] || '' };
    });
    setError('');
  };

  const categoryOptions = [
    {
      type: STUDENT_TYPES.SECONDARY,
      abbr: 'SEC',
      title: 'Secondary',
      desc: 'TVET trades for school students',
      fee: `${formatRwf(SECONDARY_TERM_FEE)} / term`,
    },
    {
      type: STUDENT_TYPES.PROFESSIONAL,
      abbr: 'PRO',
      title: 'Professional',
      desc: 'Short courses for working adults',
      fee: 'From 250K RWF',
    },
  ];

  const copyToClipboard = (text, key) => {
    navigator.clipboard.writeText(text);
    setCopied(key);
    setTimeout(() => setCopied(''), 1800);
  };

  const notifyValidation = (message, title = 'Please check your information') => {
    setError(message);
    setNotification({ open: true, title, message });
  };

  const closeNotification = () => setNotification((prev) => ({ ...prev, open: false }));

  const validateApplicantDetails = () => {
    const fullName = formData.fullName?.trim() || '';
    const email = formData.email?.trim() || '';
    const phoneDigits = (formData.phone || '').replace(/\D/g, '');

    if (fullName.length < 2) return 'Enter your full name using at least two characters.';
    if (!/^[A-Za-zÀ-ÿ\s.'-]+$/.test(fullName)) return 'Full name can only contain letters, spaces, apostrophes, dots, or hyphens.';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return 'Enter a valid email address, for example name@example.com.';
    if (phoneDigits.length < 9 || phoneDigits.length > 15) return 'Enter a valid phone number with 9 to 15 digits.';
    if (formData.dateOfBirth && new Date(`${formData.dateOfBirth}T00:00:00`) > new Date()) return 'Date of birth cannot be in the future.';
    if (formData.studentType === STUDENT_TYPES.PROFESSIONAL && formData.courses.length === 0) return 'Select at least one professional course.';
    if (formData.studentType === STUDENT_TYPES.SECONDARY && !formData.fieldOfStudy) return 'Select a trade or program for the secondary application.';
    if (formData.yearsOfExperience !== '' && (Number(formData.yearsOfExperience) < 0 || !Number.isInteger(Number(formData.yearsOfExperience)))) return 'Years of experience must be a whole number of zero or more.';

    const emergencyValues = Object.values(formData.emergencyContact || {}).filter(Boolean);
    if (emergencyValues.length > 0 && emergencyValues.length < 3) return 'Complete all emergency contact fields or leave them empty.';
    if (formData.emergencyContact?.phone && formData.emergencyContact.phone.replace(/\D/g, '').length < 9) return 'Enter a valid emergency contact phone number.';
    return '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (currentStep < 3) {
      moveToNextStep();
      return;
    }
    setError('');
    if (!formData.studentType) {
      notifyValidation('Please select a student category (Secondary or Professional).');
      return;
    }
    const applicantError = validateApplicantDetails();
    if (applicantError) {
      notifyValidation(applicantError);
      return;
    }
    const runningBatch = getRunningBatchForType(formData.studentType);
    if (!runningBatch) {
      notifyValidation('Registration is closed for this category because no active cohort is open.');
      return;
    }
    const batchStats = batchStatsMap[runningBatch._id];
    if (isBatchAtCapacity(runningBatch, batchStats)) {
      notifyValidation(getBatchCapacityMessage(runningBatch, batchStats) || 'This cohort is full.', 'Registration is temporarily unavailable');
      return;
    }
    setLoading(true);
    try {
      // Re-check capacity right before create (batch may have filled while form was open)
      try {
        const freshStats = await batchService.getBatchStats(runningBatch._id);
        setBatchStatsMap((prev) => ({ ...prev, [runningBatch._id]: freshStats }));
        if (isBatchAtCapacity(runningBatch, freshStats)) {
          notifyValidation(getBatchCapacityMessage(runningBatch, freshStats) || 'This cohort is full.', 'Registration is temporarily unavailable');
          setLoading(false);
          return;
        }
      } catch (_) { /* proceed; backend may also enforce */ }
      try {
        const existing = await studentService.findExistingByEmailOrPhone(
          formData.fullName?.trim(), formData.email?.trim(), formData.phone?.trim()
        );
        if (existing) { setDuplicateModalOpen(true); setLoading(false); return; }
      } catch (_) {}
      const studentType = formData.studentType ?? STUDENT_TYPES.SECONDARY;
      const courses = formData.courses;
      const result = await studentService.createStudent(formData);
      setRegisteredStudent({
        ...result,
        studentType: result.studentType ?? studentType,
        course: result.courses ?? result.course ?? courses,
      });
      setRegisteredFee(getTrainingFee(studentType, result.courses ?? result.course ?? courses));
      setSuccessModalOpen(true);
      setModalStep('success');
      setFormData(getInitialFormState());
      setCurrentStep(1);
    } catch (err) {
      if (isDuplicateError(err)) setDuplicateModalOpen(true);
      else notifyValidation(err.error || err.message || 'Registration failed. Please try again.', 'Registration could not be submitted');
    } finally {
      setLoading(false);
    }
  };

  const handleCloseModal = () => {
    setSuccessModalOpen(false);
    setRegisteredStudent(null);
    setRegisteredFee(null);
    setModalStep('success');
    setPaymentReceipt(null);
    setPaymentProof('');
    setPaymentSubmitted(false);
    setPaymentError('');
  };

  const sidebarFee = getTrainingFee(formData.studentType, formData.courses);
  const paymentFee = registeredFee
    ?? (registeredStudent
      ? getTrainingFee(registeredStudent.studentType, registeredStudent.course)
      : sidebarFee);
  const paymentAmountLabel = paymentFee.amount != null
    ? `${paymentFee.amountDisplay} ${paymentFee.subtitle}`
    : 'your course fee';
  const paymentFeeLabel = paymentFee.isPerCourse ? 'course fee' : 'training fee';
  const momoAmount = paymentFee.amount ?? SECONDARY_TERM_FEE;

  const activeCategory = categoryOptions.find((o) => o.type === formData.studentType) || categoryOptions[0];
  const activeBatch = getRunningBatchForType(formData.studentType);
  const activeBatchStats = activeBatch ? batchStatsMap[activeBatch._id] : null;
  const registrationBlocked = !activeBatch || isBatchAtCapacity(activeBatch, activeBatchStats);

  const moveToNextStep = () => {
    setError('');
    if (currentStep === 1) {
      if (!formData.studentType) {
        notifyValidation('Please select a programme type.');
        return;
      }
      if (registrationBlocked) {
        notifyValidation(activeBatch ? 'This cohort is full. Please select another programme or contact the center.' : 'Registration is currently closed for this programme.', 'Registration is temporarily unavailable');
        return;
      }
    }
    if (currentStep === 2) {
      const applicantError = validateApplicantDetails();
      if (applicantError) {
        notifyValidation(applicantError);
        return;
      }
    }
    setCurrentStep((step) => Math.min(step + 1, 3));
  };

  const handlePaymentSubmit = async (e) => {
    e.preventDefault();
    setPaymentError('');
    setPaymentSubmitting(true);
    try {
      if (modalStep === 'bank-transfer' && paymentReceipt)
        await paymentService.createWithReceipt(registeredStudent._id, paymentReceipt, {
          amount: momoAmount,
          method: 'bank_transfer',
        });
      else if (modalStep === 'mobile-money' && paymentProof.trim())
        await paymentService.create({
          student: registeredStudent._id,
          amount: momoAmount,
          method: 'mobile_money',
          paymentProof: paymentProof.trim(),
        });
      else { setPaymentError('Please attach a receipt or enter payment proof.'); return; }
      setPaymentSubmitted(true);
    } catch (err) {
      setPaymentError(err.error || err.message || 'Failed to submit. Please try again.');
    } finally {
      setPaymentSubmitting(false);
    }
  };

  return (
    <>
      <style>{CSS}</style>
      <div className="sr-page">

        {/* Header */}
        <header className="sr-header">
          <div className="sr-logo">
            <img src="/logo.png" alt="Fabritech Logo" className="sr-logo-image" />
            <div>
              <span className="sr-logo-text">Fabritech</span>
              <span className="sr-logo-sub">Training Center</span>
            </div>
          </div>
          <div className="sr-header-right">
            <div className="sr-header-contact">
              <a href="mailto:info@fabritech.rw">info@fabritech.rw</a>
              {' · '}
              <a href="tel:+250788601288">+250 788 601 280</a>
            </div>
            <Link to="/login" className="sr-staff-login">Staff login</Link>
            <ThemeToggle />
          </div>
        </header>

        {/* Main */}
        <div className="sr-main">
          <section className="sr-application-hero" aria-labelledby="registration-title">
            <span className="sr-form-intro-eyebrow">Open enrollment</span>
            <h2 id="registration-title">Apply to Fabritech Training Center</h2>
            <p>
              Complete your application below. Start by selecting the programme that fits your goals, then provide your details for review.
            </p>
            <div className="sr-steps" aria-label="Registration steps">
              <span className={`sr-step${currentStep === 1 ? ' sr-step--active' : ''}`}><span className="sr-step-number">1</span>Programme</span>
              <span className="sr-step-line" aria-hidden />
              <span className={`sr-step${currentStep === 2 ? ' sr-step--active' : ''}`}><span className="sr-step-number">2</span>Applicant details</span>
              <span className="sr-step-line" aria-hidden />
              <span className={`sr-step${currentStep === 3 ? ' sr-step--active' : ''}`}><span className="sr-step-number">3</span>Review</span>
            </div>
          </section>

          <div className="sr-registration-layout">

          {/* Form */}
          <div className="sr-form-card">
            <div className="sr-form-body">
            {error && <div className="sr-error">⚠ {error}</div>}

            <form onSubmit={handleSubmit}>
              {currentStep === 1 && <>
              <SectionTitle label="Program type" />
              <div className="sr-segment" role="tablist" aria-label="Student category">
                {categoryOptions.map((opt) => {
                  const isActive = formData.studentType === opt.type;
                  return (
                    <motion.button
                      key={opt.type}
                      type="button"
                      role="tab"
                      aria-selected={isActive}
                      className={`sr-segment-btn${isActive ? ' active' : ''}`}
                      onClick={() => handleSelectStudentType(opt.type)}
                      disabled={loading}
                      whileTap={loading ? {} : { scale: 0.995 }}
                    >
                      <span className="sr-segment-abbr" aria-hidden>{opt.abbr}</span>
                      <span className="sr-segment-title">{opt.title}</span>
                    </motion.button>
                  );
                })}
              </div>

              <CohortNotice activeOption={activeCategory} batch={activeBatch} batchStats={activeBatchStats} />

              </>}

              {currentStep === 2 && <>
              <SectionTitle label="Personal Information" />

              <div className="sr-form-row">
                <Field label="Full Name" required>
                  <input className="sr-input" type="text" name="fullName" value={formData.fullName}
                    onChange={handleInputChange} placeholder="Full name" required disabled={loading} />
                </Field>
                <Field label="Gender">
                  <select className="sr-select" name="gender" value={formData.gender}
                    onChange={handleInputChange} disabled={loading}>
                    <option value="">Select gender</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Other">Other</option>
                    <option value="Prefer not to say">Prefer not to say</option>
                  </select>
                </Field>
              </div>

              <div className="sr-form-row">
                <Field label="Date of Birth">
                  <input className="sr-input" type="date" name="dateOfBirth" value={formData.dateOfBirth}
                    onChange={handleInputChange} disabled={loading} />
                </Field>
                <Field label="Phone Number" required>
                  <input className="sr-input" type="tel" name="phone" value={formData.phone}
                    onChange={handleInputChange} placeholder="+250 7XX XXX XXX" required disabled={loading} />
                </Field>
              </div>

              <div className="sr-form-row">
                <Field label="Email Address" required>
                  <input className="sr-input" type="email" name="email" value={formData.email}
                    onChange={handleInputChange} placeholder="your@email.com" required disabled={loading} />
                </Field>
                <Field label="Address">
                  <input className="sr-input" type="text" name="address" value={formData.address}
                    onChange={handleInputChange} placeholder="District, Sector, Cell" disabled={loading} />
                </Field>
              </div>

              <AnimatePresence mode="wait">
              {formData.studentType === STUDENT_TYPES.SECONDARY && (
                <motion.div
                  key="secondary-fields"
                  className="sr-category-fields"
                  variants={categorySectionMotion}
                  initial="hidden"
                  animate="show"
                  exit="exit"
                >
              <SectionTitle label="Education" />

              <div className="sr-form-row sr-form-row--3">
                <Field label="Field of Study">
                  <select className="sr-select" name="fieldOfStudy" value={formData.fieldOfStudy}
                    onChange={handleInputChange} disabled={loading}>
                    <option value="">Trade / program</option>
                    {SECONDARY_TRADES.map((trade) => (
                      <option key={trade} value={trade}>{trade}</option>
                    ))}
                  </select>
                </Field>
                <Field label="School">
                  <input className="sr-input" type="text" name="schoolName" value={formData.schoolName}
                    onChange={handleInputChange} placeholder="School name" disabled={loading} />
                </Field>
                <Field label="Level">
                  <select className="sr-select" name="level" value={formData.level}
                    onChange={handleInputChange} disabled={loading}>
                    <option value="">Level</option>
                    <option value="Level 3">Level 3</option>
                    <option value="Level 4">Level 4</option>
                    <option value="Level 5">Level 5</option>
                    <option value="Other">Other</option>
                  </select>
                </Field>
              </div>

              <details className="sr-details">
                <summary>Emergency contact (optional)</summary>
                <div className="sr-details-body">
                  <div className="sr-form-row">
                    <Field label="Contact Name">
                      <input className="sr-input" type="text" name="emergencyContact.name"
                        value={formData.emergencyContact.name} onChange={handleInputChange}
                        placeholder="Full name" disabled={loading} />
                    </Field>
                    <Field label="Contact Phone">
                      <input className="sr-input" type="tel" name="emergencyContact.phone"
                        value={formData.emergencyContact.phone} onChange={handleInputChange}
                        placeholder="+250 7XX XXX XXX" disabled={loading} />
                    </Field>
                  </div>
                  <Field label="Relationship">
                    <input className="sr-input" type="text" name="emergencyContact.relation"
                      value={formData.emergencyContact.relation} onChange={handleInputChange}
                      placeholder="e.g. Parent, Guardian" disabled={loading} />
                  </Field>
                </div>
              </details>
                </motion.div>
              )}

              {formData.studentType === STUDENT_TYPES.PROFESSIONAL && (
                <motion.div
                  key="professional-fields"
                  className="sr-category-fields"
                  variants={categorySectionMotion}
                  initial="hidden"
                  animate="show"
                  exit="exit"
                >
                  <SectionTitle label="Training Course" />
                  <Field label="Courses" required>
                    <div className="sr-course-options" role="group" aria-label="Professional courses">
                      {PROFESSIONAL_COURSES.map((course) => (
                        <label className="sr-course-option" key={course}>
                          <input
                            type="checkbox"
                            checked={formData.courses.includes(course)}
                            onChange={() => handleCourseToggle(course)}
                            disabled={loading}
                          />
                          <span>{course}</span>
                        </label>
                      ))}
                    </div>
                    <span className="sr-course-hint">Select one or more courses.</span>
                  </Field>

                  <details className="sr-details">
                    <summary>Work background (optional)</summary>
                    <div className="sr-details-body">
                      <div className="sr-form-row">
                        <Field label="Profession">
                          <input
                            className="sr-input"
                            type="text"
                            name="profession"
                            value={formData.profession}
                            onChange={handleInputChange}
                            placeholder="Job title"
                            disabled={loading}
                          />
                        </Field>
                        <Field label="Employer">
                          <input
                            className="sr-input"
                            type="text"
                            name="employer"
                            value={formData.employer}
                            onChange={handleInputChange}
                            placeholder="Organization"
                            disabled={loading}
                          />
                        </Field>
                      </div>
                      <Field label="Years of Experience">
                        <input
                          className="sr-input"
                          type="number"
                          name="yearsOfExperience"
                          value={formData.yearsOfExperience}
                          onChange={handleInputChange}
                          min="0"
                          placeholder="0"
                          disabled={loading}
                        />
                      </Field>
                    </div>
                  </details>
                </motion.div>
              )}
              </AnimatePresence>
              </>}

              {currentStep === 3 && <>
                <SectionTitle label="Review your application" />
                <p className="sr-review-intro">Please confirm these details before submitting your application.</p>
                <div className="sr-review-grid">
                  <div className="sr-review-card"><span className="sr-review-label">Programme</span><div className="sr-review-value">{activeCategory.title}</div></div>
                  <div className="sr-review-card"><span className="sr-review-label">Course / trade</span><div className="sr-review-value">{formData.courses.join(', ') || formData.fieldOfStudy || 'To be confirmed'}</div></div>
                  <div className="sr-review-card"><span className="sr-review-label">Applicant</span><div className="sr-review-value">{formData.fullName}</div></div>
                  <div className="sr-review-card"><span className="sr-review-label">Contact</span><div className="sr-review-value">{formData.email}<br />{formData.phone}</div></div>
                  <div className="sr-review-card sr-review-card--wide"><span className="sr-review-label">Cohort</span><div className="sr-review-value">{activeBatch?.name || 'No active cohort available'}</div></div>
                </div>
              </>}

              <div className="sr-form-actions">
                {currentStep > 1 ? (
                  <button type="button" className="sr-back-btn" onClick={() => { setError(''); setCurrentStep((step) => step - 1); }} disabled={loading}>Back</button>
                ) : <span />}
                {currentStep < 3 ? (
                  <button type="button" className="sr-submit" onClick={moveToNextStep} disabled={loading || (currentStep === 1 && registrationBlocked)}>Continue</button>
                ) : (
              <button
                type="submit"
                className="sr-submit"
                disabled={loading || registrationBlocked}
              >
                {loading ? <><div className="sr-submit-spinner" /> Submitting…</> : registrationBlocked ? (activeBatch ? 'Cohort is full' : 'Registration closed') : 'Submit Registration'}
              </button>
                )}
              </div>
            </form>
            </div>
          </div>

          {/* Sidebar */}
          <aside className="sr-sidebar">
            <div className="sr-sidebar-heading">Your enrollment</div>
            <AnimatePresence mode="wait">
              <motion.div
                key={`${formData.studentType}-${formData.courses.join('|') || 'none'}`}
                className="sr-sidebar-fee"
                initial={{ opacity: 0, y: 12, scale: 0.98 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -10, scale: 0.98 }}
                transition={{ duration: 0.38, ease: srEase }}
              >
                <div className="sr-fee-badge">{sidebarFee.badge}</div>
                {sidebarFee.amountDisplay ? (
                  <>
                    <div className="sr-fee-amount">{sidebarFee.amountDisplay}</div>
                    <div className="sr-fee-currency">{sidebarFee.subtitle}</div>
                  </>
                ) : (
                  <>
                    <div className="sr-fee-amount" style={{ fontSize: 22 }}>Select course</div>
                    <div className="sr-fee-currency">{sidebarFee.subtitle}</div>
                  </>
                )}
                <hr className="sr-fee-divider" />
                <div className="sr-fee-note">{sidebarFee.note}</div>
              </motion.div>
            </AnimatePresence>

            <div className="sr-sidebar-features">
              <div className="sr-features-title">Why Fabritech</div>
              <ul className="sr-features-list">
                {[
                  'Expert instructors with industry experience',
                  'Hands-on, job-ready training',
                  'Recognized certification',
                  'Career support and guidance',
                ].map((text) => (
                  <li key={text}>
                    <span className="sr-feature-check" aria-hidden />
                    {text}
                  </li>
                ))}
              </ul>
            </div>
          </aside>
          </div>
        </div>

        {/* Footer */}
        <footer className="sr-footer">
          <span>© {new Date().getFullYear()} Fabritech Training Center</span>
          <div className="sr-footer-links">
            <Link to="/login">Staff login</Link>
            <a href="mailto:info@fabritech.rw">Contact</a>
          </div>
        </footer>

        {notification.open && (
          <div className="sr-overlay" role="presentation" onClick={closeNotification}>
            <div
              className="sr-modal sr-notification-modal"
              role="alertdialog"
              aria-modal="true"
              aria-labelledby="sr-notification-title"
              aria-describedby="sr-notification-message"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="sr-notification-icon" aria-hidden>!</div>
              <h2 id="sr-notification-title" className="sr-modal-title">{notification.title}</h2>
              <p id="sr-notification-message" className="sr-notification-message">{notification.message}</p>
              <button type="button" className="sr-modal-primary" onClick={closeNotification}>Review information</button>
            </div>
          </div>
        )}

        {/* ─── Duplicate Modal ─── */}
        {duplicateModalOpen && (
          <div className="sr-overlay" onClick={() => setDuplicateModalOpen(false)}>
            <div className="sr-modal" onClick={e => e.stopPropagation()}>
              <div className="sr-modal-icon sr-modal-icon--info">ℹ</div>
              <h2 className="sr-modal-title">Already Registered</h2>
              <p className="sr-modal-msg">
                A student with this name, email, or phone number already exists in our system.
              </p>
              <p className="sr-modal-sub">
                Each person may only register once. If you believe this is an error or need to update your details, please contact us at <strong>info@fabritech.rw</strong>.
              </p>
              <button className="sr-modal-primary" onClick={() => setDuplicateModalOpen(false)}>
                Got it
              </button>
            </div>
          </div>
        )}

        {/* ─── Success / Payment Modal ─── */}
        {successModalOpen && (
          <div className="sr-overlay">
            <div className="sr-modal" onClick={e => e.stopPropagation()}>

              {modalStep === 'success' && (
                <>
                  <div className="sr-modal-icon sr-modal-icon--success">✓</div>
                  <h2 className="sr-modal-title">Registration Successful!</h2>
                  <p className="sr-modal-msg">
                    Welcome{registeredStudent?.fullName ? `, ${registeredStudent.fullName}` : ''}! Your application has been received.
                  </p>
                  {registeredStudent?.batch?.name && (
                    <p className="sr-modal-sub">
                      Cohort: <strong>{registeredStudent.batch.name}</strong>
                    </p>
                  )}
                  <p className="sr-modal-sub">
                    <strong>Registration is complete.</strong> You can pay now or later at the Fabritech office — no need to pay online today.
                  </p>
                  <div className="sr-fee-box">
                    <div className="sr-fee-box-label">{paymentFee.badge} when you pay</div>
                    {paymentFee.amountDisplay ? (
                      <>
                        <div className="sr-fee-box-amount">{paymentAmountLabel}</div>
                        {paymentFee.courseLabel && (
                          <div className="sr-fee-box-course">{paymentFee.courseLabel}</div>
                        )}
                      </>
                    ) : (
                      <div className="sr-fee-box-amount">See course fee</div>
                    )}
                    <div className="sr-fee-box-note">{paymentFee.note}</div>
                  </div>
                  <button className="sr-modal-primary" onClick={handleCloseModal}>
                    Done — I'll pay later
                  </button>
                  <button className="sr-modal-secondary" onClick={() => setModalStep('payment')}>
                    Pay online now →
                  </button>
                </>
              )}

              {modalStep === 'payment' && (
                <>
                  <h2 className="sr-modal-title">Choose Payment Method</h2>
                  <p className="sr-modal-msg">Select how you'd like to pay the {paymentAmountLabel} {paymentFeeLabel}.</p>
                  <div className="sr-payment-methods">
                    <button className="sr-payment-btn" onClick={() => { setPaymentReceipt(null); setPaymentError(''); setModalStep('bank-transfer'); }}>
                      <div className="sr-payment-btn-icon">🏦</div>
                      <div className="sr-payment-btn-label">Bank Transfer</div>
                      <div className="sr-payment-btn-sub">Upload receipt</div>
                    </button>
                    <button className="sr-payment-btn" onClick={() => { setPaymentProof(''); setPaymentError(''); setModalStep('mobile-money'); }}>
                      <div className="sr-payment-btn-icon">📱</div>
                      <div className="sr-payment-btn-label">Mobile Money</div>
                      <div className="sr-payment-btn-sub">MTN MoMo</div>
                    </button>
                  </div>
                  <p className="sr-payment-note">Our team will verify your payment and confirm your enrollment within 24 hours.</p>
                  <button className="sr-modal-secondary" onClick={handleCloseModal}>Close</button>
                </>
              )}

              {modalStep === 'bank-transfer' && (
                <>
                  <button className="sr-modal-back" onClick={() => setModalStep('payment')}>← Back</button>
                  <div className="sr-modal-method-header">
                    <div className="sr-modal-method-icon">🏦</div>
                    <div>
                      <div className="sr-modal-method-title">Bank Transfer</div>
                      <div className="sr-modal-method-sub">Transfer to the account below, then upload your receipt</div>
                    </div>
                  </div>
                  <div className="sr-details-card">
                    {[
                      { label: 'Account Number', value: '000950772114574', key: 'acc' },
                      { label: 'Account Name', value: 'FabriTech Ltd', key: null },
                      { label: 'Bank', value: 'Bank of Kigali', key: null },
                      { label: 'Currency', value: 'RWF (Franc)', key: null },
                    ].map(r => (
                      <div className="sr-detail-row" key={r.label}>
                        <span className="sr-detail-lbl">{r.label}</span>
                        <span className="sr-detail-val">{r.value}</span>
                        {r.key && (
                          <button className="sr-copy-btn" onClick={() => copyToClipboard(r.value, r.key)}>
                            {copied === r.key ? '✓ Copied' : '📋 Copy'}
                          </button>
                        )}
                      </div>
                    ))}
                  </div>

                  {paymentSubmitted ? (
                    <div className="sr-success-badge">
                      <div className="sr-success-badge-icon">✅</div>
                      <div className="sr-success-badge-title">Receipt Submitted!</div>
                      <div className="sr-success-badge-text">We'll verify your payment and confirm your enrollment within 24 hours.</div>
                    </div>
                  ) : (
                    <form onSubmit={handlePaymentSubmit}>
                      {paymentError && <div className="sr-modal-error">{paymentError}</div>}
                      <label className="sr-label" style={{ display: 'block', marginBottom: 10 }}>
                        Upload Payment Receipt <span className="sr-required">*</span>
                      </label>
                      <div className={`sr-file-upload ${paymentReceipt ? 'has-file' : ''}`}>
                        <input type="file" accept="image/*,.pdf"
                          onChange={e => setPaymentReceipt(e.target.files[0] || null)}
                          disabled={paymentSubmitting} />
                        <div className="sr-file-upload-icon">{paymentReceipt ? '📎' : '⬆'}</div>
                        <div className="sr-file-upload-text">
                          {paymentReceipt ? paymentReceipt.name : 'Click to upload or drag & drop'}
                        </div>
                        <div className="sr-file-upload-sub">PNG, JPG, PDF up to 10MB</div>
                      </div>
                      <button type="submit" className="sr-modal-primary"
                        disabled={!paymentReceipt || paymentSubmitting}>
                        {paymentSubmitting ? <><div className="sr-submit-spinner" /> Submitting…</> : 'Submit Receipt'}
                      </button>
                    </form>
                  )}
                  {paymentSubmitted && (
                    <button className="sr-modal-primary" onClick={handleCloseModal}>Done</button>
                  )}
                </>
              )}

              {modalStep === 'mobile-money' && (
                <>
                  <button className="sr-modal-back" onClick={() => setModalStep('payment')}>← Back</button>
                  <div className="sr-modal-method-header">
                    <div className="sr-modal-method-icon">📱</div>
                    <div>
                      <div className="sr-modal-method-title">Mobile Money</div>
                      <div className="sr-modal-method-sub">Dial the USSD code, then paste your confirmation</div>
                    </div>
                  </div>
                  <div className="sr-momo-card">
                    <div className="sr-momo-label">MTN MoMo Pay</div>
                    <code className="sr-momo-code">*182*1*1*0787850294*AMOUNT#</code>
                    <div className="sr-momo-instruction">Replace AMOUNT with {momoAmount} (e.g. *182*8*1*025927*{momoAmount}#)</div>
                    <button className="sr-copy-btn" style={{ marginTop: 12, background: 'rgba(255,255,255,0.6)', border: '1px solid #c0daf5', padding: '7px 14px', borderRadius: 8, fontWeight: 600, fontSize: 13 }}
                      onClick={() => copyToClipboard('*182*8*1*025927*AMOUNT#', 'momo')}>
                      {copied === 'momo' ? '✓ Copied!' : '📋 Copy Code'}
                    </button>
                  </div>

                  {paymentSubmitted ? (
                    <div className="sr-success-badge">
                      <div className="sr-success-badge-icon">✅</div>
                      <div className="sr-success-badge-title">Payment Proof Submitted!</div>
                      <div className="sr-success-badge-text">We'll verify and confirm your enrollment within 24 hours.</div>
                    </div>
                  ) : (
                    <form onSubmit={handlePaymentSubmit}>
                      {paymentError && <div className="sr-modal-error">{paymentError}</div>}
                      <label className="sr-label" style={{ display: 'block', marginBottom: 10 }}>
                        Paste Confirmation Message <span className="sr-required">*</span>
                      </label>
                      <textarea
                        className="sr-proof-textarea"
                        value={paymentProof}
                        onChange={e => setPaymentProof(e.target.value)}
                        placeholder={`e.g. 'You have sent ${formatRwf(momoAmount)} RWF to Fabritech. Transaction ID: …'`}
                        rows={4}
                        required
                        disabled={paymentSubmitting}
                      />
                      <button type="submit" className="sr-modal-primary"
                        disabled={!paymentProof.trim() || paymentSubmitting}>
                        {paymentSubmitting ? <><div className="sr-submit-spinner" /> Submitting…</> : 'Submit Proof'}
                      </button>
                    </form>
                  )}
                  {paymentSubmitted && (
                    <button className="sr-modal-primary" onClick={handleCloseModal}>Done</button>
                  )}
                </>
              )}
            </div>
          </div>
        )}

      </div>
    </>
  );
};

export default StudentRegistration;
